import React from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { ContributionSideSummaryHeader } from '@components/common/ContributionSideSummaryHeader'
import { ContributionSummary } from '@components/common/ContributionSummary'
import { Dashboard } from '@components/common/Dashboard/Dashboard'
import { PageLayout } from '@components/common/PageLayout'
import { ProductsList } from '@components/common/ProductsList'
import { SideSummary } from '@components/common/SideSummary'
import { DashboardBackButton } from '@components/utils/DashboardBackButton'
import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import {
  Avatar,
  ButtonClean,
  HeadContent,
  Loading,
  ProfitString,
  Subsection,
  Summary,
  Timeline,
  useModalContext,
} from '@fourthwall/components'
import { getPrice } from '@fourthwall/utils'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'
import { useShopTime } from '@modules/Timezone/providers/ShopTimeProvider'
import { routing } from '@utils/routing'

import * as S from './Donation.styled'
import { DonationCostsBreakdownModal } from './modules/DonationCostsBreakdownModal'
import { useDonationQuery } from './queries/useDonationQuery'

export const Donation = () => {
  const location = useLocation()
  const { hasPermission } = useDashboardPermissions()
  const { open } = useModalContext()
  const { donationId = '' } = useParams<'donationId'>()
  const { donationQuery } = useDonationQuery([{ donationId }])
  const { toShopTime } = useShopTime()

  const title = donationQuery.data?.username
    ? `${donationQuery.data.username}'s donation`
    : 'Donation'
  const subtitle = donationQuery.data ? (
    <TimezonedDateString
      date={donationQuery.data.createdAt}
      format="[Placed] MMM D, YYYY [at] h:mm A"
    />
  ) : (
    ''
  )
  const value = donationQuery.data?.donation
  const profit = donationQuery.data?.profit
  const username = donationQuery.data?.username || ''
  const email = donationQuery.data?.email || ''
  const message = donationQuery.data?.message ? `"${donationQuery.data?.message}"` : ''
  const timelineEvents = donationQuery.data
    ? [
        {
          id: donationQuery.data.id,
          happenedAt: toShopTime(donationQuery.data.createdAt),
          description: `${getPrice(donationQuery.data.donation.value)} donation made by ${
            donationQuery.data.username || donationQuery.data.email
          }`,
        },
      ]
    : undefined

  const handleShowBreakdownClick = () => {
    open(DonationCostsBreakdownModal, { donationId })
  }

  const renderProfitSummaryRow = () => {
    if (!hasPermission('contributions.profit') || !profit?.value) {
      return null
    }

    return (
      <Summary.Row
        label="Your profit"
        value={
          <>
            <S.BreakdownButtonContainer>
              <ButtonClean
                label="Show breakdown"
                appearance="secondary"
                onClick={handleShowBreakdownClick}
              />
            </S.BreakdownButtonContainer>
            <ProfitString value={profit.value} />
          </>
        }
        bold
      />
    )
  }

  const renderLeftContent = () => {
    if (!value) {
      return null
    }

    return (
      <>
        <Subsection title="Order summary" borderless>
          <ContributionSummary title="Digital products / donations">
            <ContributionSummary.Section>
              <ProductsList size="large">
                <ProductsList.Item
                  name="Donation"
                  price={getPrice(value.value, { currency: value.currency })}
                  donation
                />
              </ProductsList>
            </ContributionSummary.Section>
            <ContributionSummary.Section>
              <Summary>
                <Summary.Row label="Subtotal (1 item)" value={value} />
                <Summary.Row label="Taxes" value={0} />
                <Summary.Row label="Total" value={value} />
              </Summary>
            </ContributionSummary.Section>
          </ContributionSummary>

          <S.ProfitSummaryContainer>
            <Summary>
              <Summary.Row label="Net paid by supporter" value={value} bold />
              {renderProfitSummaryRow()}
            </Summary>
          </S.ProfitSummaryContainer>
        </Subsection>
        {message && <Subsection title="Message left">{message}</Subsection>}
        {timelineEvents && (
          <Subsection title="Timeline" borderless>
            <Timeline events={timelineEvents} size="large" />
          </Subsection>
        )}
      </>
    )
  }

  const renderSummaryHeader = () => {
    if (username) {
      return <ContributionSideSummaryHeader name={username} />
    }

    return <Avatar name={email} />
  }

  const renderRightContent = () => (
    <SideSummary>
      <SideSummary.Item>{renderSummaryHeader()}</SideSummary.Item>
      <SideSummary.Item title="Contact e-mail">{email}</SideSummary.Item>
    </SideSummary>
  )

  return (
    <>
      <HeadContent title={title} />
      <Loading isLoading={donationQuery.isInitialLoading}>
        <PageLayout leftContent={renderLeftContent()} rightContent={renderRightContent()}>
          <div>
            <Dashboard.ItemViewHeader
              name={title}
              title={subtitle}
              backButton={
                <DashboardBackButton
                  label="Donations"
                  to={{
                    pathname: routing.contributions.orders.donations.all,
                    search: location.search,
                  }}
                />
              }
            />
          </div>
        </PageLayout>
      </Loading>
    </>
  )
}
